import { planClientDownscale } from "@compass/shared/utils/images";

/**
 * PRTL-1349: shrink a large photo in the browser before it is uploaded, so a
 * 12 MP phone photo attached to a post or chat goes up at ~2560 px instead of
 * full resolution. Only JPEG/PNG/WebP over 2 MB with a long edge above
 * 2560 px are touched (see planClientDownscale); everything else, and any
 * failure along the way, returns the original file unchanged.
 *
 * `createImageBitmap(..., { imageOrientation: "from-image" })` applies the
 * EXIF orientation, so the re-encoded pixels are upright and nothing relies
 * on the (stripped) orientation tag afterwards.
 */
export async function downscaleImageForUpload(file: File): Promise<File> {
    if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return file;
    let bitmap: ImageBitmap | null = null;
    try {
        bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
        const plan = planClientDownscale({
            type: file.type,
            sizeBytes: file.size,
            width: bitmap.width,
            height: bitmap.height,
        });
        if (!plan) return file;

        const canvas = document.createElement("canvas");
        canvas.width = plan.width;
        canvas.height = plan.height;
        const context = canvas.getContext("2d");
        if (!context) return file;
        context.imageSmoothingQuality = "high";
        context.drawImage(bitmap, 0, 0, plan.width, plan.height);

        const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, file.type, file.type === "image/png" ? undefined : 0.85),
        );
        // Keep the original if re-encoding didn't actually save anything.
        if (!blob || blob.size >= file.size) return file;
        return new File([blob], file.name, { type: file.type, lastModified: file.lastModified });
    } catch {
        return file;
    } finally {
        bitmap?.close();
    }
}
